import { forwardRef, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, Spinner } from "@fluentui/react-components";
import {
  ChartPie,
  Clipboard,
  Clock5,
  EllipsisVertical,
  LayoutList,
  Loader2,
  Search,
  SquareKanban,
} from "lucide-react";

import {
  AdminJobCandidateDetails,
  AdminJobDetails,
  useAdminJobDetailsQuery,
} from "@/queries/admin/job-details";
import { useGetNewCandidateMatches } from "@/mutations/admin/get-new-candidate-matches";

import dateStringToLocalDate from "@/utils/dateStringToLocalDate";
import jobStatusToColors from "@/utils/jobStatusToColors";
import formatBudgetRange from "@/utils/formatBudgetRange";

import ReadMoreSection from "@/elements/ReadMore";
import CandidateKanban from "./candidate-kanban";
import CandidateTable from "./candidate-table";
import CandidateDetailsDrawer from "./candidate-drawer";
import JobDetailsDrawer from "./job-drawer";
import VenturiBotWidget from "../venturi-bot-widget";
import AddCandidateDialog from "@/components/dialogs/opportunity/add-candidate";
import { AdminGenerateMatchesForm } from "@/components/forms/admin/job/generate-matches";

const JobDetails = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCandidate, setSelectedCandidate] =
    useState<AdminJobCandidateDetails>();
  const [showPreviousCandidateStatus, setShowPreviousCandidateStatus] =
    useState(false);
  const [searchString, setSearchString] = useState("");
  const [view, setView] = useState<"kanban" | "table">("kanban");

  const { openingId } = useParams();

  const { adminJobDetails: job, adminJobDetailsLoading } =
    useAdminJobDetailsQuery(openingId);

  useEffect(() => {
    if (selectedCandidate) {
      const selectedId = selectedCandidate.id;
      const newCandidate = job?.candidates.find((c) => c.id === selectedId);

      if (selectedCandidate !== newCandidate) {
        setSelectedCandidate(newCandidate);
      }
    }
  }, [selectedCandidate, job?.candidates]);

  const candidates = useMemo(() => {
    if (searchString) {
      const filterString = searchString.toLocaleLowerCase();

      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      return job?.candidates?.filter((candidate) =>
        candidate.name?.toLocaleLowerCase().includes(filterString),
      );
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
    } else if (job?.candidates) {
      return [...job.candidates];
    }
  }, [job, searchString]);

  const { textColor, backgroundColor } = jobStatusToColors(job?.status);

  let budgetType = "Pay Rate";
  if (job?.type === "Project" && job?.budgetType) {
    budgetType = `Pay Rate ${job.budgetType === "Monthly retainer" ? "(monthly)" : "(project)"}`;
  } else if (job?.type === "Fractional") {
    budgetType = `Pay Rate (monthly)`;
  } else if (job?.type === "Full-time") {
    budgetType = `Pay Rate (salary)`;
  }

  if (adminJobDetailsLoading || !job || !openingId) {
    return (
      <div>
        <p className="py-4 text-lg text-[#181d27] font-semibold">Jobs</p>
        <div className="w-full flex-col justify-around align-middle">
          <Spinner size="huge" />
          <p className="text-center text-base font-medium text-[#727E94]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="py-4 w-full flex flex-col gap-4 items-start self-stretch">
        <div className="pl-3 flex flex-col self-stretch justify-center">
          <div className="flex self-stretch items-center justify-between gap-2">
            <div className="flex self-stretch items-center gap-2">
              <a
                href={`/new-admin/company/${job.company.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <Avatar
                  image={{ src: job.company.logoLink }}
                  name={job.company.name}
                  className="h-[44px] w-[44px]"
                  size={48}
                />
              </a>
              <p className="text-lg text-[#181D27] font-semibold leading-6">
                {job.role}
              </p>
              <span className="px-1 text-xs text-[#71948E] bg-[#F9FBFB] border border-[#CAD3D2] rounded-3xl">
                {job.candidateCount}
              </span>
              <span
                className={cn(
                  "w-fit px-2 py-1 rounded-full",
                  "flex flex-row items-center gap-1",
                  "text-xs font-semibold",
                  job.type === "Fractional"
                    ? "bg-purple-200 text-purple-600 stroke-purple-600"
                    : job.type === "Project"
                      ? "bg-green-200 text-green-600 stroke-green-600"
                      : job.type === "Full-time"
                        ? "bg-blue-200 text-blue-600 stroke-blue-600"
                        : undefined,
                )}
              >
                {job.type === "Fractional" ? (
                  <>
                    <ChartPie size={12} />
                    Fractional
                  </>
                ) : job.type === "Project" ? (
                  <>
                    <Clipboard size={12} />
                    Project
                  </>
                ) : job.type === "Full-time" ? (
                  <>
                    <Clock5 size={12} />
                    Full-time
                  </>
                ) : undefined}
              </span>
              <div
                className={cn(
                  "py-1 px-2.5 flex flex-row items-center gap-2 rounded-full",
                  backgroundColor,
                )}
              >
                <p
                  className={cn(
                    "text-xs font-semibold tracking-[-0.24px]",
                    textColor,
                  )}
                >
                  {job.status}
                </p>
              </div>
            </div>
            <EllipsisVertical
              className="cursor-pointer"
              onClick={() => setShowDetails(true)}
            />
          </div>
          <div className="md:pl-14 flex flex-col">
            <a
              href={`/new-admin/company/${job.company.id}`}
              target="_blank"
              rel="noreferrer"
              className="text-lg text-[#646cff] font-bold hover:underline"
            >
              {job.company.name}
            </a>
            <div className="flex flex-col md:flex-row self-stretch gap-3">
              {job.type !== "Full-time" && (
                <>
                  <div className="flex flex-row gap-2 items-center">
                    <p className="text-s text-[#5C6060]">Time</p>
                    <span className="px-2 py-1 text-xs text-[#0C0D0D] border border-[#E6E6E6] rounded-full bg-[#FAFAFA]">
                      {job.scopeTime ?? "Not Specified"}
                    </span>
                  </div>
                  <div className="flex flex-row gap-2 items-center">
                    <p className="text-s text-[#5C6060]">Duration</p>
                    <span className="px-2 py-1 text-xs text-[#0C0D0D] border border-[#E6E6E6] rounded-full bg-[#FAFAFA]">
                      {job.scopeEngagementDuration ?? "Not Specified"}
                    </span>
                  </div>
                </>
              )}
              <div className="flex flex-row gap-2 items-center">
                <p className="text-s text-[#5C6060]">{budgetType}</p>
                <span className="px-2 py-1 text-xs text-[#0C0D0D] border border-[#E6E6E6] rounded-full bg-[#FAFAFA]">
                  {formatBudgetRange(job.budgetMinCents, job.budgetMaxCents) ??
                    "Not Specified"}
                </span>
              </div>
              {Boolean(job.budgetNotes) && (
                <div className="flex flex-row gap-2 items-center">
                  <p className="text-s text-[#5C6060]">Pay Notes</p>
                  <span className="px-2 py-1 text-xs text-[#0C0D0D] border border-[#E6E6E6] rounded-full bg-[#FAFAFA]">
                    {job.budgetNotes ?? "Not Specified"}
                  </span>
                </div>
              )}
              <div className="flex flex-row gap-2 items-center">
                <p className="text-s text-[#5C6060]">Start Date</p>
                <span className="px-2 py-1 text-xs text-[#0C0D0D] border border-[#E6E6E6] rounded-full bg-[#FAFAFA]">
                  {job.desiredStartDate
                    ? dateStringToLocalDate(
                        job.desiredStartDate,
                      )?.toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })
                    : "Not Specified"}
                </span>
              </div>
            </div>
            <ReadMoreSection
              maxLines={3}
              textClassName="text-[#5C6060]"
              readMoreClassName="font-semibold text-[#6941C6]"
            >
              {job.aIVjoDescription}
            </ReadMoreSection>
          </div>
        </div>
        <div className="flex flex-col md:flex-row mnd:items-center justify-between self-stretch gap-2">
          <div className="flex flex-row items-center gap-4">
            <p className="text-lg text-[#181d27] font-semibold">Candidates</p>
            <GenerateMatchesButton job={job} />
            <AddCandidateDialog jobOpening={job} />
          </div>
          <div className="flex flex-row items-center gap-3">
            <div
              className={cn(
                "flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder="Search for a candidate"
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
            <div className="flex flex-row gap-1">
              <Label className="font-normal">Show Before Closed</Label>
              <Switch
                checked={showPreviousCandidateStatus}
                onCheckedChange={setShowPreviousCandidateStatus}
              />
            </div>
            <div className="flex flex-row items-start bg-[#F9F9FB] rounded-lg">
              <div
                className={cn(
                  "h-10 w-10 flex items-center justify-center rounded-[10px]",
                  "cursor-pointer hover:bg-white hover:border hover:border-[#E5E5EC] hover:shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]",
                  view === "kanban" &&
                    "bg-white border border-[#E5E5EC] shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]",
                )}
                onClick={() => setView("kanban")}
              >
                <SquareKanban />
              </div>
              <div
                className={cn(
                  "h-10 w-10 flex items-center justify-center rounded-[10px]",
                  "cursor-pointer hover:bg-white hover:border hover:border-[#E5E5EC] hover:shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]",
                  view === "table" &&
                    "bg-white border border-[#E5E5EC] shadow-[0px_1px_2px_0px_rgba(82,88,102,0.06)]",
                )}
                onClick={() => setView("table")}
              >
                <LayoutList />
              </div>
            </div>
          </div>
        </div>
        {view === "kanban" && (
          <CandidateKanban
            openingId={openingId}
            candidates={candidates}
            showPreviousState={showPreviousCandidateStatus}
            setSelectedCandidate={setSelectedCandidate}
          />
        )}
        {view === "table" && (
          <CandidateTable
            openingId={openingId}
            candidates={candidates}
            showPreviousState={showPreviousCandidateStatus}
            setSelectedCandidate={setSelectedCandidate}
          />
        )}
      </div>
      <CandidateDetailsDrawer
        job={job}
        candidate={selectedCandidate}
        showPreviousState={showPreviousCandidateStatus}
        setOpen={() => setSelectedCandidate(undefined)}
      />
      <JobDetailsDrawer
        job={job}
        open={showDetails}
        setOpen={() => setShowDetails(false)}
      />
      <VenturiBotWidget job={job} />
    </div>
  );
};

type BaseButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

interface UpdateCandidateButtonProps extends BaseButtonProps {
  job: AdminJobDetails;
}

const GenerateMatchesButton = forwardRef<
  HTMLButtonElement,
  UpdateCandidateButtonProps
>(({ job, ...buttonProps }, ref) => {
  const [open, setOpen] = useState(false);

  const { getNewCandidateMatches, isGettingNewCandidateMatches } =
    useGetNewCandidateMatches(job.id);

  const submitFn = useMemo(
    () => (data: { count: number }) => {
      getNewCandidateMatches(data);
      setOpen(false);
    },
    [setOpen, getNewCandidateMatches],
  );

  const handleClick = () => {
    setOpen(true);
  };

  const awaitingResult = Boolean(
    // Either waiting for the press to return
    isGettingNewCandidateMatches ||
      // Or waiting for a queued job to finish
      (job.candidateGenerationBlockedUntil &&
        new Date(job.candidateGenerationBlockedUntil) > new Date()),
  );

  // If the job is closed, hide the button
  if (
    job.status === "Role Filled (won)" ||
    job.status === "Role Filled (lost)"
  ) {
    return;
  }

  return (
    <>
      <Button
        {...buttonProps}
        ref={ref}
        variant="secondary"
        disabled={awaitingResult}
        onClick={handleClick}
      >
        {awaitingResult ? <Loader2 className="animate-spin" /> : null}
        Generate Matches
      </Button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="w-full sm:max-w-2xl">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <DialogTitle>Generate matches</DialogTitle>
              </div>
              <DialogDescription className="sr-only">
                Please choose how many new matches should be generated
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
              <AdminGenerateMatchesForm
                onSubmit={submitFn}
                onCancel={() => setOpen(false)}
              />
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
});
GenerateMatchesButton.displayName = "GenerateMatchesButton";

export default JobDetails;
